import { createAsyncThunk } from '@reduxjs/toolkit';

import { AdminApiPaths } from '../../../api/APIPaths';
import APIDataProvider, {
  TApiPaginationResponse,
  TApiResponse,
} from '../../../api/ApiDataProvider';
import {
  ICourseTutor,
  ICourseTutorDeleteVerificationData,
  TCourseTutorType,
  TCourseTutorVerifyDelete,
} from '.';

export type TGetCourseTutorsAsAdmin = {
  course: string;
  limit: number;
  offset: number;
  status?: ICourseTutor['status'];
};

export type TCreateCourseTutor = {
  course: string;
  tutor: string;
  title?: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  type: TCourseTutorType;
  tutorRole?: string;
  tutorRateAmount?: number;
  tutorRateCurrency?: string;
};

export type TUpdateCourseTutor = {
  id: string;
  course: string;
  status?: string;
  title?: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  type?: TCourseTutorType;
  tutorRole?: string;
  tutorRateAmount?: number;
  tutorRateCurrency?: string;
};

export type TVerifyDeleteCourseTutor = {
  id: string;
  type: TCourseTutorVerifyDelete;
};

export type TDeleteCourseTutor = {
  id: string;
};

const apiDataProvider = new APIDataProvider();

const getCourseTutorsAsAdminModule = async ({
  course,
  limit,
  offset,
  status,
}: TGetCourseTutorsAsAdmin) => {
  let path = `${AdminApiPaths.courseTutors}?limit=${limit}&offset=${offset}`;

  if (course) {
    path += `&course=${course}`;
  }

  if (status) {
    path += `&status=${status}`;
  }

  const response = await apiDataProvider.get<
    TApiResponse<TApiPaginationResponse<ICourseTutor>>
  >(path);

  return response.data;
};

export const getCourseTutorsAsAdmin = createAsyncThunk(
  '/admin/course-tutors/list',
  getCourseTutorsAsAdminModule
);

export const createCourseTutor = createAsyncThunk(
  '/admin/course-tutors/create',
  async (data: TCreateCourseTutor) => {
    const path = `${AdminApiPaths.courseTutors}`;

    const response = await apiDataProvider.post<
      TApiResponse<ICourseTutor & { groupClassId: string }>
    >(path, data);

    return response.data;
  }
);

export const deleteCourseTutor = createAsyncThunk(
  '/admin/course-tutors/delete',
  async (data: { id: string; course: string }) => {
    const path = `${AdminApiPaths.courseTutors}/${data.id}?course=${data.course}`;

    const response = await apiDataProvider.delete<TApiResponse<ICourseTutor>>(
      path
    );

    return response.data;
  }
);

export const updateCourseTutor = createAsyncThunk(
  '/admin/course-tutors/update',
  async ({ id, ...data }: TUpdateCourseTutor) => {
    const path = `${AdminApiPaths.courseTutors}/${id}`;

    const response = await apiDataProvider.put<TApiResponse<ICourseTutor>>(
      path,
      data
    );

    return response.data;
  }
);

export const verifyCourseTutorDelete = createAsyncThunk(
  '/admin/course-tutors/verify-delete',
  async ({ id, type }: TVerifyDeleteCourseTutor) => {
    const path = `${AdminApiPaths.courseTutors}/verify-delete/${id}?id=${id}&type=${type}`;

    const response = await apiDataProvider.get<
      TApiResponse<ICourseTutorDeleteVerificationData>
    >(path);

    return response.data;
  }
);
